import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton } from '../../shared/components';
import { useLenderDiligenceRequestsQuery } from '../../shared/hooks';
import { CurrentUser, DataRoomDiligenceRequest } from '../../shared/models';
import { AddQuestionForm } from './AddQuestionForm';
import { AddRequestDocumentForm } from './AddRequestDocumentForm';
import { DeleteDiligenceRequestDialog } from './DeleteDiligenceRequestDialog';
import { DiligenceRequestsList } from './DiligenceRequestsList';
import { EditDiligenceRequestForm } from './EditDiligenceRequestForm';
import { AssociateDocumentsDrawer } from './AssociateDocumentsDrawer';

interface LenderDiligenceRequestsPageProps {
	currentUser: CurrentUser;
	isTeamMember: boolean;
}

export const LenderDiligenceRequestsPage: React.FC<LenderDiligenceRequestsPageProps> = ({
	currentUser,
	isTeamMember,
}) => {
	const { guid } = useParams<'guid'>();
	const requestsQuery = useLenderDiligenceRequestsQuery(guid);
	const [tab, setTab] = React.useState('pending');
	const [addQuestionRequest, setAddQuestionRequest] = React.useState<boolean>(false);
	const [addDocumentRequest, setAddDocumentRequest] = React.useState<boolean>(false);
	const [editRequest, setEditRequest] = React.useState<DataRoomDiligenceRequest | null>(null);
	const [deleteRequest, setDeleteRequest] = React.useState<DataRoomDiligenceRequest | null>(null);
	const [associateDocuments, setAssociateDocuments] = React.useState<DataRoomDiligenceRequest | null>(null);

	const closeDrawersAndDialogs = () => {
		setAddQuestionRequest(false);
		setAddDocumentRequest(false);
		setEditRequest(null);
		setDeleteRequest(null);
	};

	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	return (
		<CerebroPageContainer maxWidth={'100%'}>
			<CerebroBreadcrumb>Diligence Requests</CerebroBreadcrumb>

			<Box sx={{ marginBottom: 2 }}>
				<CerebroSecondaryButton onClick={() => setAddQuestionRequest(true)}>
					Ask a Question
				</CerebroSecondaryButton>
				<CerebroSecondaryButton onClick={() => setAddDocumentRequest(true)} sx={{ marginLeft: 1 }}>
					Request Document(s)
				</CerebroSecondaryButton>
			</Box>

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Pending" value="pending" />
						<Tab label="Completed" value="completed" />
						<Tab label="All" value="all" />
					</TabList>
				</Box>
				<TabPanel value="pending">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => !w.isCompleted && !w.isWithdrawn)}
						currentUser={currentUser}
						isTeamMember={isTeamMember}
						tab="pending"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
				<TabPanel value="completed">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => w.isCompleted)}
						currentUser={currentUser}
						isTeamMember={isTeamMember}
						tab="completed"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
				<TabPanel value="all">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => !w.isWithdrawn)}
						currentUser={currentUser}
						isTeamMember={isTeamMember}
						tab="all"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
			</TabContext>

			<AddQuestionForm dataRoomGuid={guid} open={addQuestionRequest} close={closeDrawersAndDialogs} />
			<AddRequestDocumentForm dataRoomGuid={guid} open={addDocumentRequest} close={closeDrawersAndDialogs} />
			<EditDiligenceRequestForm dataRoomGuid={guid} request={editRequest} close={closeDrawersAndDialogs} />
			<DeleteDiligenceRequestDialog
				dataRoomGuid={guid}
				request={deleteRequest}
				currentUser={currentUser}
				close={closeDrawersAndDialogs}
			/>
			<AssociateDocumentsDrawer dataRoomGuid={guid} request={associateDocuments} close={closeDrawersAndDialogs} />
		</CerebroPageContainer>
	);
};
